import type { Difficulty } from "@/domain";

import { createHabitsLevel } from "./create-habits-level";

export interface SchoolScenarioNarration {
  text: string;
  audio: string;
}

export interface SchoolScenarioChoice {
  id: string;
  text: string;
  audio: string;
  safety: "safe" | "unsafe";
  supportiveFeedback: SchoolScenarioNarration;
}

export interface SchoolScenarioPayload {
  narration: SchoolScenarioNarration;
  choices: SchoolScenarioChoice[];
  bestChoiceId: string;
  followUp?: SchoolScenarioNarration;
}

interface ScenarioLevelInput {
  difficulty: Difficulty;
  objectives: string[];
  title: string;
  scene: string;
  choices: Array<
    Omit<SchoolScenarioChoice, "audio" | "supportiveFeedback"> & {
      supportiveFeedback: Omit<SchoolScenarioNarration, "audio">;
    }
  >;
  bestChoiceId: string;
  followUp?: string;
}

function createScenarioLevel({
  difficulty,
  objectives,
  title,
  scene,
  choices,
  bestChoiceId,
  followUp
}: ScenarioLevelInput) {
  const instructionAudio =
    "/audio/habits/school-scenarios-instruction-" + difficulty + ".ogg";
  const sceneAudio =
    "/audio/habits/school-scenarios-scene-" + difficulty + ".ogg";
  const audioPrefix =
    "/audio/habits/school-scenarios-" + String(difficulty) + "-";

  return createHabitsLevel<SchoolScenarioPayload>({
    activityType: "school-scenarios",
    difficulty,
    objectives,
    title,
    instruction: scene + "你会怎么做？",
    promptAudio: instructionAudio,
    payload: {
      narration: { text: scene, audio: sceneAudio },
      choices: choices.map((choice) => ({
        ...choice,
        audio: audioPrefix + "choice-" + choice.id + ".ogg",
        supportiveFeedback: {
          ...choice.supportiveFeedback,
          audio: audioPrefix + "feedback-" + choice.id + ".ogg"
        }
      })),
      bestChoiceId,
      ...(followUp === undefined
        ? {}
        : {
            followUp: {
              text: followUp,
              audio: audioPrefix + "follow-up.ogg"
            }
          })
    }
  });
}

export const schoolScenarioLevels = [
  createScenarioLevel({
    difficulty: 1,
    objectives: ["练习听完整的课堂提示", "知道轮流表达的办法"],
    title: "先听老师说完",
    scene: "老师正讲着今天的活动，你也想到了一个好主意。",
    choices: [
      {
        id: "listen-then-raise-hand",
        text: "先认真听，等老师说完再举手。",
        safety: "safe",
        supportiveFeedback: {
          text: "先听完，再举手。这样你能听清要求，也能把想法好好说出来。"
        }
      },
      {
        id: "speak-right-away",
        text: "马上大声说出自己的想法。",
        safety: "safe",
        supportiveFeedback: {
          text: "好主意先在心里等一小会儿。听老师说完，再举手分享，大家都能听清。"
        }
      }
    ],
    bestChoiceId: "listen-then-raise-hand"
  }),
  createScenarioLevel({
    difficulty: 2,
    objectives: ["遵守课堂轮流发言的规则", "尊重正在表达的同学"],
    title: "我也知道答案",
    scene: "同学正在回答问题，你也知道答案，很想说出来。",
    choices: [
      {
        id: "raise-hand-and-wait",
        text: "安静举手，等老师请你回答。",
        safety: "safe",
        supportiveFeedback: {
          text: "你把说话的时间留给了同学。等老师请你时，再把答案说出来。"
        }
      },
      {
        id: "call-out-answer",
        text: "抢先把答案说出来。",
        safety: "safe",
        supportiveFeedback: {
          text: "答案先在心里等一等。让同学说完，你再举手分享。"
        }
      },
      {
        id: "hurry-classmate",
        text: "催同学快一点回答。",
        safety: "safe",
        supportiveFeedback: {
          text: "每个人都需要一点思考时间。安静等一等，轮到你时再分享。"
        }
      }
    ],
    bestChoiceId: "raise-hand-and-wait"
  }),
  createScenarioLevel({
    difficulty: 3,
    objectives: ["能向老师说明自己的需要", "学习照顾身体发出的信号"],
    title: "我想去洗手间",
    scene: "上课时，你的身体悄悄提醒你：该去洗手间了。",
    choices: [
      {
        id: "tell-teacher",
        text: "举手告诉老师自己的需要，得到回应后再去。",
        safety: "safe",
        supportiveFeedback: {
          text: "把需要告诉老师，老师就能陪你把这件事安排好。"
        }
      },
      {
        id: "keep-waiting-silently",
        text: "一直忍着，什么也不说。",
        safety: "unsafe",
        supportiveFeedback: {
          text: "身体在提醒你时，可以马上告诉老师，不用一个人忍着。"
        }
      },
      {
        id: "leave-without-telling",
        text: "不告诉老师，自己离开教室。",
        safety: "unsafe",
        supportiveFeedback: {
          text: "先让老师知道你要去哪里，得到回应后再去洗手间。"
        }
      }
    ],
    bestChoiceId: "tell-teacher",
    followUp: "想去洗手间时，可以告诉老师；在家时，家长也可以帮助你。"
  }),
  createScenarioLevel({
    difficulty: 4,
    objectives: ["在校园里选择合适的求助对象", "能清楚说明自己需要帮助"],
    title: "暂时找不到教室",
    scene: "从洗手间出来，你转了个弯，一时找不到自己的教室。",
    choices: [
      {
        id: "ask-duty-teacher",
        text: "留在明亮、有人经过的地方，请值班老师帮忙。",
        safety: "safe",
        supportiveFeedback: {
          text: "你留在容易被看见的地方，又找到了能帮忙的老师。这样更容易回到教室。"
        }
      },
      {
        id: "walk-far-away",
        text: "一个人一直往远处走，边走边找。",
        safety: "unsafe",
        supportiveFeedback: {
          text: "先别越走越远。留在附近，找佩戴工作牌的老师帮忙。"
        }
      },
      {
        id: "wait-without-speaking",
        text: "站在角落里等，但不告诉任何人。",
        safety: "unsafe",
        supportiveFeedback: {
          text: "把需要说出来吧：告诉值班老师，你找不到教室了。"
        }
      }
    ],
    bestChoiceId: "ask-duty-teacher",
    followUp: "找不到路时，可以求助老师、值班人员或家长，不必一个人去找。"
  }),
  createScenarioLevel({
    difficulty: 5,
    objectives: ["掌握有信号灯路口的过街步骤", "过马路时和同行大人保持联系"],
    title: "和大人一起过马路",
    scene: "你牵着大人的手走到有信号灯的路口，准备过马路。",
    choices: [
      {
        id: "wait-check-and-walk",
        text: "停在路边，牵好大人的手，等绿灯亮并确认车辆停下后再走。",
        safety: "safe",
        supportiveFeedback: {
          text: "你把每一步都记住了：停在路边，等绿灯，确认车辆停下，再和大人一起走。"
        }
      },
      {
        id: "run-when-road-looks-empty",
        text: "看到路上暂时没有车就跑过去。",
        safety: "unsafe",
        supportiveFeedback: {
          text: "路口暂时没车，也要停下等绿灯，再和大人一起确认车辆停下。"
        }
      },
      {
        id: "watch-toy-while-walking",
        text: "一边看手里的玩具，一边往前走。",
        safety: "unsafe",
        supportiveFeedback: {
          text: "小玩具先回到口袋里。看着信号灯和车辆，牵好大人的手。"
        }
      },
      {
        id: "rush-on-green-light",
        text: "绿灯刚亮就马上冲出去。",
        safety: "unsafe",
        supportiveFeedback: {
          text: "绿灯亮了也先看一看。确认车辆停下，再和大人稳稳走过去。"
        }
      }
    ],
    bestChoiceId: "wait-check-and-walk",
    followUp: "过马路时，牵好大人的手，看信号灯和车辆，确认车辆停下再走。"
  }),
  createScenarioLevel({
    difficulty: 6,
    objectives: ["物品靠近车道时先停下", "知道请老师或大人协助处理"],
    title: "球滚到车道边",
    scene: "在校门附近，皮球骨碌碌滚到了车道边。",
    choices: [
      {
        id: "stop-and-ask-adult",
        text: "停在人行区域，请身边的老师或大人帮忙取球。",
        safety: "safe",
        supportiveFeedback: {
          text: "你停在安全的人行区域，又请大人来帮忙。皮球交给大人处理就好。"
        }
      },
      {
        id: "run-after-ball",
        text: "马上跑到车道边捡球。",
        safety: "unsafe",
        supportiveFeedback: {
          text: "皮球可以等一等，你先留在人行区域，请老师或大人帮忙。"
        }
      },
      {
        id: "send-classmate",
        text: "请同学跑过去捡球。",
        safety: "unsafe",
        supportiveFeedback: {
          text: "小朋友都不要靠近车道。大家留在人行区域，请老师或大人帮忙。"
        }
      },
      {
        id: "reach-from-roadside",
        text: "站在车道边伸手去够球。",
        safety: "unsafe",
        supportiveFeedback: {
          text: "先退回人行区域，和大人待在一起，请大人来取球。"
        }
      }
    ],
    bestChoiceId: "stop-and-ask-adult",
    followUp: "物品滚到车道附近时，留在人行区域，请老师或大人来处理。"
  })
];
